import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MailCompose } from '../components/mail/MailCompose';
import { MailSidebar } from '../components/mail/MailSidebar';
import { MailThreadList } from '../components/mail/MailThreadList';
import { MailThreadView, type ComposeRequest, type ThreadChange } from '../components/mail/MailThreadView';
import { ApiError } from '../lib/apiClient';
import { listMailboxes, setThreadState } from '../lib/mailApi';
import { buildReplySeed, emptySeed, type ComposeSeed } from '../lib/mailCompose';
import type { MailFolder, MailThreadDetailDto, MailThreadSummaryDto } from '../lib/mailTypes';
import { useApiResource } from '../lib/useApiResource';
import { useMailLive, type MailChange } from '../lib/useMailLive';
import { useMailThreads } from '../lib/useMailThreads';

/** Webmail (spec 008, M2 inbox + M3 compose/reply/forward). Never log or persist anything from message content. */
export function MailPage() {
  const mailboxes = useApiResource(listMailboxes, []);
  const [mailboxId, setMailboxId] = useState<string | null>(null);
  const [folder, setFolder] = useState<MailFolder>('inbox');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [starredOnly, setStarredOnly] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [compose, setCompose] = useState<{ seed: ComposeSeed; key: number } | null>(null);
  const [threadRefresh, setThreadRefresh] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const composeDirty = useRef(false);
  const composeCounter = useRef(0);

  const list = useMailThreads(mailboxId, folder);
  const { reload: reloadMailboxes } = mailboxes;

  useEffect(() => {
    if (!mailboxId && mailboxes.data?.length) setMailboxId(mailboxes.data[0].id);
  }, [mailboxes.data, mailboxId]);

  const visible = useMemo(
    () => (starredOnly ? list.threads.filter((t) => t.starred) : list.threads),
    [list.threads, starredOnly],
  );

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const onDirtyChange = useCallback((dirty: boolean) => {
    composeDirty.current = dirty;
  }, []);

  const openCompose = (seed: ComposeSeed) => {
    if (compose && composeDirty.current && !window.confirm('Discard the message you are writing?')) return;
    composeDirty.current = false;
    composeCounter.current += 1;
    setCompose({ seed, key: composeCounter.current });
  };

  const ownAddresses = useMemo(() => (mailboxes.data ?? []).map((m) => m.address), [mailboxes.data]);

  const onComposeFromThread: ComposeRequest = (mode, thread, message) => {
    const known = mailboxes.data?.some((m) => m.id === thread.mailboxId);
    openCompose(
      buildReplySeed({ mode, thread, message, ownAddresses, mailboxId: known ? thread.mailboxId : (mailboxId ?? undefined) }),
    );
  };

  // Live updates: the backend touches a content-free per-mailbox document when mail arrives or is
  // sent; refresh the badges, the folder being viewed and the open conversation without a reload.
  const mailboxIds = useMemo(() => (mailboxes.data ?? []).map((m) => m.id), [mailboxes.data]);
  const onMailChange = useCallback(
    ({ mailboxId: changed, kind }: MailChange) => {
      reloadMailboxes();
      if (changed === '' || changed === mailboxId) {
        list.reload();
        if (selectedId) setThreadRefresh((n) => n + 1);
      }
      if (kind === 'inbound') {
        const address = mailboxes.data?.find((m) => m.id === changed)?.address;
        setToast(address ? `New mail in ${address}` : 'New mail');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mailboxId, selectedId, mailboxes.data, list.reload, reloadMailboxes],
  );
  useMailLive(mailboxIds, onMailChange);

  const onSent = () => {
    setCompose(null);
    setToast('Message sent');
    list.reload();
    reloadMailboxes();
    setThreadRefresh((n) => n + 1);
  };

  const selectMailbox = (id: string) => {
    setMailboxId(id);
    setSelectedId(null);
  };
  const selectFolder = (next: MailFolder) => {
    setFolder(next);
    setSelectedId(null);
  };

  const onLoaded = useCallback(
    (detail: MailThreadDetailDto) => {
      // The server marked it read; reflect that in the row and the unread badge.
      list.patchThread(detail.thread);
      reloadMailboxes();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list.patchThread, reloadMailboxes],
  );

  const onChanged = useCallback(
    ({ summary, leave }: ThreadChange) => {
      // Sent lists every conversation that contains a message sent from this mailbox, wherever it
      // lives now (a reply stays in its inbox thread), so it only leaves Sent when trashed.
      const stays = folder === 'sent' ? summary.folder !== 'trash' : summary.folder === folder;
      if (!stays) list.removeThread(summary.id);
      else list.patchThread(summary);
      if (leave) setSelectedId(null);
      reloadMailboxes();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [folder, list.patchThread, list.removeThread, reloadMailboxes],
  );

  const toggleStar = async (thread: MailThreadSummaryDto) => {
    setActionError(null);
    try {
      list.patchThread(await setThreadState(thread.id, { starred: !thread.starred }));
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not update the star');
    }
  };

  return (
    <div className="mail-page">
      <div className="section-header">
        <div>
          <h1>Mail</h1>
          <p className="cell-sub">Company inboxes for @mediclynk.com.</p>
        </div>
      </div>
      <div className="mail-policy-banner" role="note">
        Do not send or store patient health information (PHI) by email.
      </div>
      {mailboxes.error && (
        <div role="alert" className="error-banner">
          {mailboxes.error}{' '}
          <button type="button" className="btn btn-xs" onClick={mailboxes.reload}>
            Retry
          </button>
        </div>
      )}
      {actionError && (
        <div role="alert" className="error-banner">
          {actionError}
        </div>
      )}
      {mailboxes.loading && !mailboxes.data && (
        <div className="mail-list-state" role="status">
          Loading mailboxes…
        </div>
      )}
      {mailboxes.data && mailboxes.data.length === 0 && (
        <div className="empty-state">You do not have access to any mailbox yet.</div>
      )}
      {mailboxes.data && mailboxes.data.length > 0 && (
        <div className={selectedId ? 'mail-layout has-thread' : 'mail-layout'}>
          <MailSidebar
            mailboxes={mailboxes.data}
            mailboxId={mailboxId}
            folder={folder}
            starredOnly={starredOnly}
            onSelectMailbox={selectMailbox}
            onSelectFolder={selectFolder}
            onToggleStarred={() => setStarredOnly((v) => !v)}
            onCompose={() => openCompose(emptySeed(mailboxId ?? mailboxes.data![0].id))}
          />
          <section className="mail-list-pane" aria-label="Conversation list">
            <MailThreadList
              threads={visible}
              selectedId={selectedId}
              loading={list.loading}
              loadingMore={list.loadingMore}
              error={list.error}
              hasMore={list.hasMore}
              onSelect={setSelectedId}
              onToggleStar={(t) => void toggleStar(t)}
              onLoadMore={list.loadMore}
              onRetry={list.reload}
            />
          </section>
          <section className="mail-reading-pane" aria-label="Conversation">
            {selectedId ? (
              <MailThreadView
                key={`${selectedId}:${threadRefresh}`}
                threadId={selectedId}
                onBack={() => setSelectedId(null)}
                onLoaded={onLoaded}
                onChanged={onChanged}
                onCompose={onComposeFromThread}
              />
            ) : (
              <div className="mail-list-state empty-state">Select a conversation to read it.</div>
            )}
          </section>
        </div>
      )}
      {mailboxes.data && mailboxes.data.length > 0 && compose && (
        <MailCompose
          key={compose.key}
          mailboxes={mailboxes.data}
          seed={compose.seed}
          onClose={() => setCompose(null)}
          onSent={onSent}
          onDirtyChange={onDirtyChange}
        />
      )}
      {toast && (
        <div className="mail-toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}
